import { PERSIST, PURGE, REHYDRATE } from 'redux-persist/es/constants'
import { waitingRoomReducerBlackListActions } from '../store/syncBlackLists/waitingRoomReducerBlackList'
import { patientSidePanelReducerBlackListActions } from '../store/syncBlackLists/patientSidePanelReducerBlackList'
import { visitsReducerBlackListActions } from '../store/syncBlackLists/visitsReducerBlackList'
import { accountSettingsReducerBlackList } from '../store/syncBlackLists/accountSettingsReducerBlackList'
import { calendarReducerBlackListActions } from '../store/syncBlackLists/calendarReducerBlackList'
import { chartsReducerBlackList } from '../store/syncBlackLists/chartsReducerBlackList'
import { loginPageReducerBlackList } from '../store/syncBlackLists/loginPageReducerBlackList'
import { filePopUpConstantsBlackList } from '../store/file-pop-up/FilePopUpConstants'
import { productsBlackList } from '../store/products/productsConstants'
import { idVerificationPopUpConstantsBlackList } from '../store/id-verification-pop-up/IDVerificationPopUpConstants'
import { LOGOUT } from '../store/auth/AuthConstants'
import { SET_FLAGS } from '../store/flags/FlagsConstants'

export const LISTENER = 'listenerMiddleware/add'
export const EMPTY_ACTION = 'common/EMPTY_ACTION'

/**
 * LOGOUT reasons that SHOULD end the session in every open tab.
 *
 * `LOGOUT` used to be broadcast unconditionally, so a background tab that merely hit a
 * network blip while refreshing its token would tear down the tab holding a live video
 * visit — resetting `WaitingRoomReducer` and `ChartsReducer` and losing the note. Only a
 * deliberate sign-out, a backend rejection of the session, or a shared idle timeout (all
 * tabs read the same `lastUserAction`) are legitimate reasons to log every tab out.
 *
 * Anything else — an untagged logout, or either of the refresh-failure paths — stays
 * local to the tab that saw the problem. `logoutReportingMiddleware` records propagation
 * as `crossTab`, so the effect of this list is verifiable in Sentry after the fact.
 */
export const CROSS_TAB_LOGOUT_REASONS = [
  'manual',
  'session-expired',
  'inactivity-timeout',
  // Same control as 'inactivity-timeout', just evaluated at mount instead of on a timer:
  // both read the one origin-shared `lastUserAction` key and both conclude the provider
  // has been idle past the 30-minute window. If that shared key says everyone is idle,
  // the conclusion is equally true in every tab, so splitting them would be arbitrary.
  'mount-inactivity-check',
  // `AuthValidate` swapping one session for another. Its paired `LOGIN_SUCCESS` is already
  // broadcast, so leaving this local would hand the other tabs the new provider's token on
  // top of the previous provider's cached profile, queue and charts. It syncs, but it does
  // not end the session: `logoutTeardownMiddleware` exempts it from the storage teardown,
  // which would otherwise reload every tab to `/` right after the new login.
  'session-replaced',
]

const blacklist = [
  PERSIST,
  PURGE,
  REHYDRATE,
  LISTENER,
  EMPTY_ACTION,
  // Every tab reads Remote Config for itself, so relaying this only re-renders the other tabs
  // with a value they already computed — and a flag pinned in one tab via DevTools must not
  // leak into another.
  SET_FLAGS,
  ...loginPageReducerBlackList,
  ...waitingRoomReducerBlackListActions,
  ...patientSidePanelReducerBlackListActions,
  ...accountSettingsReducerBlackList,
  ...calendarReducerBlackListActions,
  ...visitsReducerBlackListActions,
  ...chartsReducerBlackList,
  ...filePopUpConstantsBlackList,
  ...productsBlackList,
  ...idVerificationPopUpConstantsBlackList,
]

/** Should this action be broadcast to the other tabs by `redux-state-sync`? */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const shouldSyncAction = (action) => {
  if (typeof action === 'function' || action === null || typeof action !== 'object') {
    return false
  }
  // Sync a logout to other tabs only when the session genuinely ended.
  if (action.type === LOGOUT) {
    return CROSS_TAB_LOGOUT_REASONS.includes(action.reason)
  }
  return !blacklist.includes(action.type)
}
